import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import SystemConfig from '../components/SystemConfig';
import api from '../utils/api';
import toast from 'react-hot-toast';
import { PageTitle, Button, Card, Icons } from '../shared/ui';

const INPUT_CLS = 'w-full h-9 px-3 text-sm border border-stone-300 rounded-md bg-white text-stone-900 focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600 disabled:bg-stone-50 disabled:text-stone-500';
const LABEL_CLS = 'block text-xs font-medium text-stone-600 mb-1';
const SELECT_CLS = 'w-full h-9 px-3 text-sm border border-stone-300 rounded-md bg-white text-stone-900 focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600';

const TABS = [
  { id: 'profile',      name: 'Profile',       icon: <Icons.User size={18} />,     roles: ['admin', 'manager', 'renter'] },
  { id: 'security',     name: 'Security',      icon: <Icons.Shield size={18} />,   roles: ['admin', 'manager', 'renter'] },
  { id: 'notifications',name: 'Notifications', icon: <Icons.Bell size={18} />,     roles: ['admin', 'manager', 'renter'] },
  { id: 'preferences',  name: 'Preferences',   icon: <Icons.Cog size={18} />,      roles: ['admin', 'manager', 'renter'] },
  { id: 'api-profiles', name: 'API Profiles',  icon: <Icons.Upload size={18} />,   roles: ['admin', 'manager'] },
  { id: 'system',       name: 'System Config', icon: <Icons.Cog size={18} />,      roles: ['admin'] },
];

const Settings = () => {
  const { t } = useTranslation(['settings', 'common']);
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('profile');
  const [saved, setSaved] = useState(false);

  const tabs = TABS.filter(t => t.roles.includes(user?.role));

  const handleSave = () => {
    setSaved(true);
    setTimeout(() => setSaved(false), 3000);
  };

  return (
    <div className="space-y-6 pb-12">
      <PageTitle
        title={t('settings:title')}
        subtitle={t('settings:subtitle')}
        icon={<Icons.Cog />}
      />

      <div className="bg-white rounded-2xl shadow border border-stone-100 overflow-hidden flex flex-col md:flex-row min-h-[600px]">
        {/* Sidebar */}
        <div className="md:w-64 bg-stone-50 border-r border-stone-100 p-4">
          <nav className="space-y-1">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`w-full flex items-center px-3 py-2.5 text-sm font-medium rounded-xl transition-all ${
                  activeTab === tab.id
                    ? 'bg-teal-600 text-white shadow'
                    : 'text-stone-600 hover:bg-white hover:shadow hover:text-stone-900'
                }`}
              >
                <span className={`mr-3 ${activeTab === tab.id ? 'text-white' : 'text-stone-400'}`}>
                  {tab.icon}
                </span>
                {tab.name}
              </button>
            ))}
          </nav>
        </div>

        {/* Content */}
        <div className="flex-1 p-8 bg-white overflow-x-auto">
          {saved && (
            <div className="bg-green-50 text-green-700 border border-green-100 rounded-xl px-4 py-3 flex items-center shadow-sm mb-6">
              <Icons.CheckCircle2 size={18} className="mr-2 text-green-500 shrink-0" />
              <span className="font-medium text-sm">Settings saved successfully!</span>
            </div>
          )}

          {activeTab === 'profile'       && <ProfileSettings user={user} onSave={handleSave} />}
          {activeTab === 'security'      && <SecuritySettings onSave={handleSave} />}
          {activeTab === 'notifications' && <NotificationSettings onSave={handleSave} />}
          {activeTab === 'preferences'   && <PreferencesSettings onSave={handleSave} />}
          {activeTab === 'api-profiles'  && <APIProfilesSettings />}
          {activeTab === 'system'        && <SystemConfig />}
        </div>
      </div>
    </div>
  );
};

// ── Profile Settings ──────────────────────────────────────────────────────────
const ProfileSettings = ({ user, onSave }) => {
  const [formData, setFormData] = useState({
    firstName: user?.first_name || '',
    lastName:  user?.last_name  || '',
    email:     user?.email      || '',
    phone:     user?.phone      || '',
    idNumber:  user?.id_number  || '',
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (e) => setFormData({ ...formData, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.put(`/api/users/${user.id}`, {
        first_name: formData.firstName,
        last_name:  formData.lastName,
        email:      formData.email,
        phone:      formData.phone,
        id_number:  formData.idNumber,
      });
      toast.success(t('settings:toast.profileUpdated'));
      if (onSave) onSave();
    } catch (error) {
      toast.error(error.response?.data?.detail || t('settings:toast.profileUpdateFailed'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <h3 className="text-lg font-bold text-stone-800 mb-6 pb-4 border-b border-stone-100">Profile Settings</h3>

      <div className="flex items-center gap-4 mb-6">
        <div className="h-20 w-20 rounded-full bg-teal-50 border-4 border-white shadow flex items-center justify-center">
          <Icons.User size={36} className="text-teal-300" />
        </div>
        <div>
          <button type="button" className="px-3 py-1.5 bg-white border border-stone-200 rounded-lg text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors shadow-sm">
            Change Photo
          </button>
          <p className="text-xs text-stone-400 mt-1">JPG, GIF or PNG. Max size of 800K</p>
        </div>
      </div>

      <form className="space-y-5 max-w-2xl" onSubmit={handleSubmit}>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={LABEL_CLS}>First Name</label>
            <input type="text" name="firstName" value={formData.firstName} onChange={handleChange} className={INPUT_CLS} />
          </div>
          <div>
            <label className={LABEL_CLS}>Last Name</label>
            <input type="text" name="lastName" value={formData.lastName} onChange={handleChange} className={INPUT_CLS} />
          </div>
        </div>

        <div>
          <label className={LABEL_CLS}>Email Address <span className="text-stone-400 font-normal">(optional)</span></label>
          <div className="relative">
            <input type="email" name="email" value={formData.email} onChange={handleChange}
              placeholder="your.email@example.com"
              className="w-full h-9 pl-9 pr-3 text-sm border border-stone-300 rounded-md bg-white text-stone-900 focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600"
            />
            <Icons.Mail size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
          </div>
          <p className="text-xs text-stone-400 mt-1">Email is optional. You can also login with your username.</p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={LABEL_CLS}>Phone Number</label>
            <div className="relative">
              <input type="tel" name="phone" value={formData.phone} onChange={handleChange}
                className="w-full h-9 pl-9 pr-3 text-sm border border-stone-300 rounded-md bg-white text-stone-900 focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600"
              />
              <Icons.Phone size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            </div>
          </div>
          <div>
            <label className={LABEL_CLS}>ID Number</label>
            <div className="relative">
              <input type="text" name="idNumber" value={formData.idNumber} onChange={handleChange}
                className="w-full h-9 pl-9 pr-3 text-sm border border-stone-300 rounded-md bg-white text-stone-900 focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600"
              />
              <Icons.Document size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-stone-400" />
            </div>
          </div>
        </div>

        <div className="pt-4 border-t border-stone-100">
          <Button kind="primary" loading={saving} type="submit">Save Profile Changes</Button>
        </div>
      </form>
    </div>
  );
};

// ── Security Settings ─────────────────────────────────────────────────────────
const SecuritySettings = ({ onSave }) => {
  const { user } = useAuth();
  const [formData, setFormData] = useState({ currentPassword: '', newPassword: '', confirmPassword: '', twoFactorEnabled: false });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({ ...formData, [name]: type === 'checkbox' ? checked : value });
    setError('');
  };

  const handlePasswordChange = async () => {
    setError('');
    if (!formData.currentPassword || !formData.newPassword || !formData.confirmPassword) {
      setError('All password fields are required'); return;
    }
    if (formData.newPassword !== formData.confirmPassword) {
      setError('New passwords do not match'); return;
    }
    if (formData.newPassword.length < 6) {
      setError('New password must be at least 6 characters'); return;
    }
    setLoading(true);
    try {
      const token = localStorage.getItem('access_token');
      const apiUrl = import.meta.env.VITE_API_URL || '/api';
      const res = await fetch(`${apiUrl}/users/${user.id}/change-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify({ old_password: formData.currentPassword, new_password: formData.newPassword }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to change password');
      setFormData({ currentPassword: '', newPassword: '', confirmPassword: '', twoFactorEnabled: formData.twoFactorEnabled });
      onSave();
    } catch (err) {
      setError(err.message || 'Failed to change password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <h3 className="text-lg font-bold text-stone-800 mb-6 pb-4 border-b border-stone-100">Security Settings</h3>
      <form className="space-y-8 max-w-2xl" onSubmit={(e) => e.preventDefault()}>
        <div>
          <h4 className="text-sm font-semibold text-stone-700 mb-4 flex items-center gap-2">
            <Icons.Lock size={16} className="text-teal-500" /> Change Password
          </h4>
          {error && (
            <div className="mb-4 bg-red-50 border border-red-100 text-red-600 px-4 py-3 rounded-xl flex items-center text-sm gap-2">
              <Icons.Alert size={16} className="text-red-500 shrink-0" />
              {error}
            </div>
          )}
          <div className="space-y-4">
            <div>
              <label className={LABEL_CLS}>Current Password</label>
              <input type="password" name="currentPassword" value={formData.currentPassword} onChange={handleChange} className={INPUT_CLS} disabled={loading} />
            </div>
            <div>
              <label className={LABEL_CLS}>New Password</label>
              <input type="password" name="newPassword" value={formData.newPassword} onChange={handleChange} className={INPUT_CLS} disabled={loading} />
            </div>
            <div>
              <label className={LABEL_CLS}>Confirm New Password</label>
              <input type="password" name="confirmPassword" value={formData.confirmPassword} onChange={handleChange} className={INPUT_CLS} disabled={loading} />
            </div>
          </div>
          <div className="mt-4">
            <Button kind="primary" loading={loading} onClick={handlePasswordChange}>Update Password</Button>
          </div>
        </div>

        <div className="pt-6 border-t border-stone-100">
          <h4 className="text-sm font-semibold text-stone-700 mb-4 flex items-center gap-2">
            <Icons.Shield size={16} className="text-green-500" /> Two-Factor Authentication
          </h4>
          <label className="flex items-center p-4 bg-stone-50 rounded-xl border border-stone-100 hover:bg-white hover:shadow-sm transition-all cursor-pointer">
            <input
              type="checkbox" id="twoFactor" name="twoFactorEnabled"
              checked={formData.twoFactorEnabled} onChange={handleChange}
              className="accent-teal-600 h-4 w-4 cursor-pointer"
            />
            <div className="ml-3">
              <span className="text-sm font-medium text-stone-700 block">Enable two-factor authentication (2FA)</span>
              <span className="text-xs text-stone-500">Add an extra layer of security to your account</span>
            </div>
          </label>
        </div>
      </form>
    </div>
  );
};

// ── Notification Settings ─────────────────────────────────────────────────────
const NotificationSettings = ({ onSave }) => {
  const [settings, setSettings] = useState({
    emailNotifications: true, telegramNotifications: true, pushNotifications: false,
    newTicketAlert: true, contractExpiration: true, paymentReminder: true,
    maintenanceUpdate: false, cvAlerts: true,
  });

  const handleChange = (e) => setSettings({ ...settings, [e.target.name]: e.target.checked });

  const CHANNELS = [
    { id: 'emailNotifications',    label: 'Email Notifications',    icon: <Icons.Mail size={16} /> },
    { id: 'telegramNotifications', label: 'Telegram Notifications', icon: <Icons.Send size={16} /> },
    { id: 'pushNotifications',     label: 'Push Notifications',     icon: <Icons.Bell size={16} /> },
  ];
  const ALERTS = [
    { id: 'newTicketAlert',    label: 'New Ticket Alerts' },
    { id: 'contractExpiration',label: 'Contract Expiration' },
    { id: 'paymentReminder',   label: 'Payment Reminders' },
    { id: 'cvAlerts',          label: 'Security Alerts (CV)' },
  ];

  return (
    <div>
      <h3 className="text-lg font-bold text-stone-800 mb-6 pb-4 border-b border-stone-100">Notification Preferences</h3>
      <div className="space-y-8 max-w-2xl">
        <div>
          <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Channels</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {CHANNELS.map(item => (
              <label key={item.id} className="flex items-center p-4 bg-stone-50 border border-stone-100 rounded-xl hover:bg-white hover:shadow-sm transition-all cursor-pointer gap-3">
                <input type="checkbox" name={item.id} checked={settings[item.id]} onChange={handleChange} className="accent-teal-600 h-4 w-4" />
                <span className="text-stone-400">{item.icon}</span>
                <span className="text-sm font-medium text-stone-700">{item.label}</span>
              </label>
            ))}
          </div>
        </div>

        <div>
          <h4 className="text-xs font-semibold text-stone-500 uppercase tracking-wider mb-3">Alert Types</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {ALERTS.map(item => (
              <label key={item.id} className="flex items-center justify-between p-4 bg-stone-50 border border-stone-100 rounded-xl hover:bg-white hover:shadow-sm transition-all cursor-pointer">
                <span className="text-sm font-medium text-stone-700">{item.label}</span>
                <input type="checkbox" name={item.id} checked={settings[item.id]} onChange={handleChange} className="accent-teal-600 h-4 w-4" />
              </label>
            ))}
          </div>
        </div>

        <div className="pt-2">
          <Button kind="primary" onClick={onSave}>Save Notification Settings</Button>
        </div>
      </div>
    </div>
  );
};

// ── Preferences Settings ──────────────────────────────────────────────────────
const PreferencesSettings = ({ onSave }) => {
  const { t } = useTranslation(['settings', 'common']);
  const { user } = useAuth();
  const { setTheme: applyTheme } = useTheme();
  const [preferences, setPreferences] = useState({ language: 'en', timezone: 'UTC', dateFormat: 'MM/DD/YYYY', theme: 'light' });
  const [loading, setLoading] = useState(false);
  const [loadingPrefs, setLoadingPrefs] = useState(true);
  const [error, setError] = useState('');

  React.useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem('access_token');
        const apiUrl = import.meta.env.VITE_API_URL || '/api';
        const res = await fetch(`${apiUrl}/users/${user.id}/preferences`, { headers: { 'Authorization': `Bearer ${token}` } });
        if (res.ok) {
          const data = await res.json();
          setPreferences(data);
          if (data.theme) applyTheme(data.theme);
        }
      } catch (err) {
        console.error('Failed to load preferences:', err);
      } finally {
        setLoadingPrefs(false);
      }
    };
    if (user?.id) load();
  }, [user]);

  const handleChange = (e) => setPreferences({ ...preferences, [e.target.name]: e.target.value });

  const handleSave = async () => {
    setLoading(true); setError('');
    try {
      const token = localStorage.getItem('access_token');
      const apiUrl = import.meta.env.VITE_API_URL || '/api';
      const res = await fetch(`${apiUrl}/users/${user.id}/preferences`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
        body: JSON.stringify(preferences),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to save preferences');
      applyTheme(preferences.theme);
      onSave();
    } catch (err) {
      setError(err.message || 'Failed to save preferences');
    } finally {
      setLoading(false);
    }
  };

  if (loadingPrefs) {
    return (
      <div className="flex items-center justify-center py-10">
        <Icons.Spinner size={24} className="animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-lg font-bold text-stone-800 mb-6 pb-4 border-b border-stone-100">{t('settings:prefs.title')}</h3>
      {error && <div className="mb-4 bg-red-50 border border-red-100 text-red-600 px-4 py-3 rounded-xl text-sm">{error}</div>}
      <div className="space-y-4 max-w-2xl">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className={LABEL_CLS}>{t('settings:prefs.language.label')}</label>
            <select name="language" value={preferences.language} onChange={handleChange} className={SELECT_CLS}>
              <option value="en">{t('settings:prefs.language.en')}</option>
              <option value="vi">{t('settings:prefs.language.vi')}</option>
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>{t('settings:prefs.timezone.label')}</label>
            <select name="timezone" value={preferences.timezone} onChange={handleChange} className={SELECT_CLS}>
              <option value="UTC">{t('settings:prefs.timezone.utc')}</option>
              <option value="Asia/Ho_Chi_Minh">{t('settings:prefs.timezone.vietnam')}</option>
              <option value="Asia/Bangkok">{t('settings:prefs.timezone.bangkok')}</option>
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>{t('settings:prefs.dateFormat.label')}</label>
            <select name="dateFormat" value={preferences.dateFormat} onChange={handleChange} className={SELECT_CLS}>
              <option value="MM/DD/YYYY">{t('settings:prefs.dateFormat.us')}</option>
              <option value="DD/MM/YYYY">{t('settings:prefs.dateFormat.intl')}</option>
              <option value="YYYY-MM-DD">{t('settings:prefs.dateFormat.iso')}</option>
            </select>
          </div>
          <div>
            <label className={LABEL_CLS}>{t('settings:prefs.theme.label')}</label>
            <select name="theme" value={preferences.theme} onChange={handleChange} className={SELECT_CLS}>
              <option value="light">{t('settings:prefs.theme.light')}</option>
              <option value="dark">{t('settings:prefs.theme.dark')}</option>
              <option value="auto">{t('settings:prefs.theme.auto')}</option>
            </select>
          </div>
        </div>
        <div className="pt-4 border-t border-stone-100">
          <Button kind="primary" loading={loading} onClick={handleSave}>{t('settings:prefs.save')}</Button>
        </div>
      </div>
    </div>
  );
};

// ── API Documentation (intentionally dark-themed code reference) ───────────────
const APIDocumentation = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [expandedEndpoint, setExpandedEndpoint] = useState(null);
  const [copiedCode, setCopiedCode] = useState(null);

  const copyCode = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    setTimeout(() => setCopiedCode(null), 2000);
  };

  const baseUrl = window.location.origin;

  const CopyBtn = ({ text, id }) => (
    <button onClick={() => copyCode(text, id)} className="flex items-center gap-1 text-slate-400 hover:text-teal-400 transition-colors text-xs ml-2">
      {copiedCode === id
        ? <><Icons.CheckCircle2 size={14} className="text-green-400" /><span className="text-green-400">Copied!</span></>
        : <><Icons.Copy size={14} /><span>Copy</span></>}
    </button>
  );

  const MethodBadge = ({ method }) => {
    const colors = { GET: 'bg-emerald-500/20 text-emerald-400', POST: 'bg-blue-500/20 text-blue-400', PUT: 'bg-amber-500/20 text-amber-400', DELETE: 'bg-rose-500/20 text-rose-400' };
    return <span className={`px-2 py-0.5 text-xs font-bold rounded font-mono ${colors[method] || 'bg-slate-500/20 text-slate-400'}`}>{method}</span>;
  };

  const endpointGroups = [
    {
      id: 'auth', label: 'Authentication', color: 'violet',
      endpoints: [{
        method: 'POST', path: '/api/token', name: 'Obtain Access Token',
        description: 'Exchange username and password for a JWT Bearer token. Token encodes sub, user_id, and role (admin | manager | renter). Only admin and manager roles can access finance data.',
        auth: 'none', authNote: 'No auth required',
        params: [
          { name: 'username', type: 'string', required: true, in: 'body', description: 'User login name' },
          { name: 'password', type: 'string', required: true, in: 'body', description: 'User password' },
        ],
        response: `{ "access_token": "eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9...", "token_type": "bearer" }`
      }]
    },
    {
      id: 'payments', label: 'Payments', color: 'indigo',
      endpoints: [
        { method: 'GET', path: '/api/payments', name: 'List Payments', description: 'List all payments with optional filters. Returns full payment detail including utilities, adjustments, and status. Accessible via Bearer token OR API key with finance.read permission.', auth: 'both-read', authNote: 'Bearer  OR  API-Key (finance.read)', params: [{ name: 'room_id', type: 'integer', required: false, in: 'query', description: 'Filter by room ID' }, { name: 'building_id', type: 'integer', required: false, in: 'query', description: 'Filter by building ID' }, { name: 'month', type: 'string', required: false, in: 'query', description: 'Filter by month (YYYY-MM)' }, { name: 'payment_status', type: 'string', required: false, in: 'query', description: 'pending | partial | confirmed | completed | rejected | failed | refunded' }, { name: 'skip', type: 'integer', required: false, in: 'query', description: 'Pagination offset (default 0)' }, { name: 'limit', type: 'integer', required: false, in: 'query', description: 'Max records to return (default 100)' }], response: `[{\n  "id": 1, "room_id": 51, "month": "2025-01",\n  "renter_name": "Nguyen Van A",\n  "base_rent": 3500000,\n  "electric_cost": 875000, "water_cost": 225000,\n  "other_costs": 50000,\n  "invoice_amount": 4650000,\n  "adjustments_total": -200000,\n  "final_amount": 4450000,\n  "paid_amount": 4450000,\n  "status": "confirmed",\n  "due_date": "2025-01-10",\n  "paid_date": "2025-01-08"\n}]` },
        { method: 'GET', path: '/api/payments/{payment_id}', name: 'Get Payment by ID', description: 'Full payment detail including adjustments array. Bearer token only.', auth: 'bearer', authNote: 'Bearer only', params: [{ name: 'payment_id', type: 'integer', required: true, in: 'path', description: 'Payment ID' }], response: `{ "id": 1, "room_id": 51, "month": "2025-01", "adjustments": [...], ... }` },
        { method: 'GET', path: '/api/payments/room/{room_id}', name: 'Get Payments by Room', description: 'All payment history for a specific room. Bearer token only.', auth: 'bearer', authNote: 'Bearer only', params: [{ name: 'room_id', type: 'integer', required: true, in: 'path', description: 'Room ID' }], response: `[{ "id": 1, "month": "2025-01", "final_amount": 4450000, "status": "confirmed" }, ...]` },
        { method: 'GET', path: '/api/payments/room/{room_id}/month/{month}', name: 'Get Payment by Room & Month', description: 'Single payment for a specific room and month. Bearer token only.', auth: 'bearer', authNote: 'Bearer only', params: [{ name: 'room_id', type: 'integer', required: true, in: 'path', description: 'Room ID' }, { name: 'month', type: 'string', required: true, in: 'path', description: 'Month in YYYY-MM format' }], response: `{ "id": 1, "room_id": 51, "month": "2025-01", "final_amount": 4450000, "status": "confirmed" }` },
        { method: 'GET', path: '/api/payments/grouped', name: 'Payments Grouped by Building', description: 'Per-building totals: invoiced, collected, outstanding, collection rate. Accessible via Bearer token OR API key with finance.read permission.', auth: 'both-read', authNote: 'Bearer  OR  API-Key (finance.read)', params: [{ name: 'month', type: 'string', required: false, in: 'query', description: 'Filter by month (YYYY-MM)' }], response: `[{\n  "building_id": 1, "building_name": "Building A",\n  "total_invoiced": 110000000,\n  "total_paid": 104000000,\n  "outstanding": 6000000,\n  "collection_rate": 94.55,\n  "total_rooms": 30, "occupied": 28\n}]` },
      ]
    },
    {
      id: 'transactions', label: 'Payment Transactions', color: 'cyan',
      endpoints: [
        { method: 'GET', path: '/api/payments/{payment_id}/transactions', name: 'List Transactions for Payment', description: 'All payment transactions for a given payment ID. Bearer token only.', auth: 'bearer', authNote: 'Bearer only', params: [{ name: 'payment_id', type: 'integer', required: true, in: 'path', description: 'Payment ID' }], response: `[{\n  "id": 1, "payment_id": 1,\n  "submitted_amount": 4450000, "confirmed_amount": 4450000,\n  "proof_url": "https://...", "notes": "Bank transfer",\n  "status": "confirmed",\n  "submitted_at": "2025-01-08T09:00:00",\n  "confirmed_at": "2025-01-08T10:30:00"\n}]` },
        { method: 'GET', path: '/api/payments/{payment_id}/transactions/{transaction_id}', name: 'Get Single Transaction', description: 'Full detail of a single payment transaction. Bearer token only.', auth: 'bearer', authNote: 'Bearer only', params: [{ name: 'payment_id', type: 'integer', required: true, in: 'path', description: 'Payment ID' }, { name: 'transaction_id', type: 'integer', required: true, in: 'path', description: 'Transaction ID' }], response: `{ "id": 1, "submitted_amount": 4450000, "status": "confirmed", ... }` },
      ]
    },
    {
      id: 'invoices', label: 'Invoices', color: 'blue',
      endpoints: [
        { method: 'GET', path: '/api/invoices', name: 'List Invoices', description: 'All invoices with optional status and date filters. Accessible via Bearer token OR API key with finance.read permission.', auth: 'both-read', authNote: 'Bearer  OR  API-Key (finance.read)', params: [{ name: 'status', type: 'string', required: false, in: 'query', description: 'pending | paid | overdue | cancelled' }, { name: 'skip', type: 'integer', required: false, in: 'query', description: 'Pagination offset' }, { name: 'limit', type: 'integer', required: false, in: 'query', description: 'Max records' }], response: `[{\n  "id": 1,\n  "invoice_number": "INV-2025-001",\n  "contract_id": 5, "renter_id": 12,\n  "issue_date": "2025-01-01",\n  "due_date": "2025-01-10",\n  "amount": 4650000, "paid_amount": 4650000,\n  "status": "paid",\n  "line_items": {\n    "rent": 3500000, "electricity": 875000,\n    "water": 225000, "internet": 50000\n  }\n}]` },
        { method: 'GET', path: '/api/invoices/{invoice_id}', name: 'Get Invoice by ID', description: 'Full invoice detail with all line items. Bearer token only.', auth: 'bearer', authNote: 'Bearer only', params: [{ name: 'invoice_id', type: 'integer', required: true, in: 'path', description: 'Invoice ID' }], response: `{ "id": 1, "invoice_number": "INV-2025-001", "line_items": {...}, "status": "paid" }` },
      ]
    },
    {
      id: 'meter-readings', label: 'Meter Readings', color: 'teal',
      endpoints: [
        { method: 'GET', path: '/api/meter-readings', name: 'List Meter Readings', description: 'Electricity (kWh) and water (m³) readings with usage and cost calculations. Accessible via Bearer token OR API key with finance.read permission.', auth: 'both-read', authNote: 'Bearer  OR  API-Key (finance.read)', params: [{ name: 'room_id', type: 'integer', required: false, in: 'query', description: 'Filter by room' }, { name: 'month', type: 'string', required: false, in: 'query', description: 'Filter by month (YYYY-MM)' }, { name: 'reading_type', type: 'string', required: false, in: 'query', description: 'electricity | water' }, { name: 'status', type: 'string', required: false, in: 'query', description: 'pending | verified | rejected' }], response: `[{\n  "id": 1, "room_id": 51, "month": "2025-01",\n  "reading_type": "electricity",\n  "previous_reading": 1250, "reading_value": 1380,\n  "usage_amount": 130, "unit_price": 4000,\n  "total_cost": 520000,\n  "status": "verified"\n}]` },
        { method: 'GET', path: '/api/meter-readings/room/{room_id}/history', name: 'Room Meter History', description: 'Historical meter readings for a specific room. Bearer token only.', auth: 'bearer', authNote: 'Bearer only', params: [{ name: 'room_id', type: 'integer', required: true, in: 'path', description: 'Room ID' }, { name: 'reading_type', type: 'string', required: false, in: 'query', description: 'electricity | water' }], response: `[{ "month": "2025-01", "usage_amount": 130, "total_cost": 520000 }, ...]` },
      ]
    },
    {
      id: 'expenses', label: 'Expenses', color: 'orange',
      endpoints: [
        { method: 'GET', path: '/api/expenses', name: 'List Expenses', description: 'All operational and overhead expenses. Accessible via Bearer token OR API key with finance.read permission.', auth: 'both-read', authNote: 'Bearer  OR  API-Key (finance.read)', params: [{ name: 'building_id', type: 'integer', required: false, in: 'query', description: 'Filter by building' }, { name: 'category', type: 'string', required: false, in: 'query', description: 'Expense category key' }, { name: 'start_date', type: 'string', required: false, in: 'query', description: 'Start date (YYYY-MM-DD)' }, { name: 'end_date', type: 'string', required: false, in: 'query', description: 'End date (YYYY-MM-DD)' }], response: `[{\n  "id": 1, "title": "Electricity Bill - Jan 2025",\n  "amount": 1200000, "category": "electric",\n  "expense_date": "2025-01-31",\n  "building_name": "Building A"\n}]` },
        { method: 'GET', path: '/api/expenses/stats', name: 'Expense Statistics', description: 'Aggregated expense totals, per-category sums, and monthly trend data.', auth: 'both-read', authNote: 'Bearer  OR  API-Key (finance.read)', params: [{ name: 'year', type: 'integer', required: false, in: 'query', description: 'Filter by year' }, { name: 'building_id', type: 'integer', required: false, in: 'query', description: 'Filter by building' }], response: `{\n  "total_amount": 8500000,\n  "total_count": 15,\n  "by_category": {\n    "electric": 3200000, "water": 800000,\n    "maintenance": 2500000, "internet": 600000\n  }\n}` },
        { method: 'GET', path: '/api/expenses/categories/standard', name: 'Standard Expense Categories', description: 'Canonical list of expense category keys and labels.', auth: 'both-read', authNote: 'Bearer  OR  API-Key (finance.read)', params: [], response: `[{ "key": "electric", "label": "Electricity" }, ...]` },
      ]
    },
    {
      id: 'adjustments', label: 'Adjustments', color: 'amber',
      endpoints: [
        { method: 'GET', path: '/api/adjustments', name: 'List Adjustments', description: 'All payment adjustments (credits, waivers, discounts). Accessible via Bearer token OR API key with finance.read permission.', auth: 'both-read', authNote: 'Bearer  OR  API-Key (finance.read)', params: [{ name: 'payment_id', type: 'integer', required: false, in: 'query', description: 'Filter by payment' }, { name: 'building_id', type: 'integer', required: false, in: 'query', description: 'Filter by building' }, { name: 'month', type: 'string', required: false, in: 'query', description: 'Filter by month (YYYY-MM)' }, { name: 'status', type: 'string', required: false, in: 'query', description: 'applied | pending | reversed' }], response: `[{\n  "id": 1, "payment_id": 1,\n  "service_id": "internet",\n  "adjustment_type": "discount",\n  "adjustment_percent": 50,\n  "adjustment_amount": -25000,\n  "reason": "Promotional discount",\n  "status": "applied"\n}]` },
        { method: 'POST', path: '/api/adjustments', name: 'Create Adjustment', description: 'Apply a credit, waiver, or discount to a payment or building. Write operations always require Bearer token.', auth: 'bearer', authNote: 'Bearer only (write operation)', params: [{ name: 'payment_id', type: 'integer', required: false, in: 'body', description: 'Target single payment' }, { name: 'service_id', type: 'string', required: true, in: 'body', description: 'internet | elevator | cleaning | washing' }, { name: 'adjustment_type', type: 'string', required: true, in: 'body', description: 'credit | waiver | discount' }, { name: 'adjustment_percent', type: 'number', required: true, in: 'body', description: 'Percentage to reduce (0-100)' }, { name: 'reason', type: 'string', required: true, in: 'body', description: 'Reason for adjustment' }], response: `{\n  "created": 12,\n  "adjustments": [...],\n  "summary": { "total_adjustment_amount": -300000, "affected_payments": 12 }\n}` },
      ]
    },
    {
      id: 'stats', label: 'Stats & P&L', color: 'emerald',
      endpoints: [
        { method: 'GET', path: '/api/stats', name: 'Finance Summary Stats', description: 'Top-level KPIs: total revenue, pending, overdue, collection rate, invoice counts.', auth: 'both-read', authNote: 'Bearer  OR  API-Key (finance.read)', params: [{ name: 'month', type: 'string', required: false, in: 'query', description: 'Month (YYYY-MM), defaults to current' }], response: `{\n  "total_revenue": 38500000,\n  "pending_amount": 4500000,\n  "overdue_amount": 2000000,\n  "collection_rate": 85.56,\n  "total_invoices": 15,\n  "paid_invoices": 12,\n  "pending_invoices": 3\n}` },
        { method: 'GET', path: '/api/stats/monthly', name: 'Monthly Stats', description: 'Detailed stats for a specific year/month.', auth: 'both-read', authNote: 'Bearer  OR  API-Key (finance.read)', params: [{ name: 'year', type: 'integer', required: false, in: 'query', description: 'Year (defaults to current)' }, { name: 'month', type: 'integer', required: false, in: 'query', description: 'Month 1-12 (defaults to current)' }], response: `{ "year": 2025, "month": 1, "total_revenue": 38500000, "expenses": 8500000, "net_profit": 30000000 }` },
        { method: 'GET', path: '/api/stats/profit', name: 'Profit & Loss Statement', description: 'Full P&L: gross invoice amount, adjustments, expected revenue, actual collected, total expenses, net profit, margin.', auth: 'both-read', authNote: 'Bearer  OR  API-Key (finance.read)', params: [{ name: 'year', type: 'integer', required: false, in: 'query', description: 'Year filter' }, { name: 'month', type: 'integer', required: false, in: 'query', description: 'Month filter' }], response: `{\n  "gross_invoice_amount": 45000000,\n  "total_adjustments": -500000,\n  "expected_revenue": 44500000,\n  "total_revenue": 38500000,\n  "total_expenses": 8500000,\n  "net_profit": 30000000,\n  "profit_margin": 77.92\n}` },
      ]
    },
    {
      id: 'analytics', label: 'Analytics', color: 'purple',
      endpoints: [
        { method: 'GET', path: '/api/analytics/revenue', name: 'Revenue Dashboard', description: 'Current month & YTD totals, expenses, net income, per-building breakdown, monthly trend.', auth: 'both-read', authNote: 'Bearer  OR  API-Key (finance.read)', params: [{ name: 'year', type: 'integer', required: false, in: 'query', description: 'Year filter' }, { name: 'month', type: 'integer', required: false, in: 'query', description: 'Month filter' }], response: `{\n  "current_month": {\n    "total_invoiced": 45000000, "total_collected": 38500000,\n    "final_expected": 44500000, "total_expenses": 8500000, "net_income": 30000000\n  },\n  "ytd": { "total_invoiced": 540000000, "total_collected": 462000000 },\n  "by_building": [...],\n  "monthly_trend": [...]\n}` },
        { method: 'GET', path: '/api/analytics/revenue/by-service', name: 'Revenue by Service', description: 'Income breakdown per service type (rent, electricity, water, internet, etc.).', auth: 'both-read', authNote: 'Bearer  OR  API-Key (finance.read)', params: [{ name: 'year', type: 'integer', required: false, in: 'query', description: 'Year filter' }, { name: 'month', type: 'integer', required: false, in: 'query', description: 'Month filter' }], response: `[{ "service": "rent", "label": "Base Rent", "invoiced": 30000000, "confirmed": 28000000 }, ...]` },
        { method: 'GET', path: '/api/analytics/profit/by-category', name: 'P&L by Category', description: 'Pairs each income service with its matching expense category — gross margin and margin %.', auth: 'both-read', authNote: 'Bearer  OR  API-Key (finance.read)', params: [{ name: 'year', type: 'integer', required: false, in: 'query', description: 'Year filter' }, { name: 'month', type: 'integer', required: false, in: 'query', description: 'Month filter' }], response: `[{ "category": "electric", "income": 8750000, "expense": 3200000, "gross_margin": 5550000, "margin_percent": 63.43 }, ...]` },
        { method: 'GET', path: '/api/analytics/adjustments', name: 'Adjustments Analytics', description: 'Aggregate waiver/credit/discount summaries, monthly trend, building breakdown.', auth: 'both-read', authNote: 'Bearer  OR  API-Key (finance.read)', params: [{ name: 'year', type: 'integer', required: false, in: 'query', description: 'Year filter' }, { name: 'month', type: 'integer', required: false, in: 'query', description: 'Month filter' }], response: `{ "total_amount": -500000, "total_count": 8, "by_type": {...}, "monthly_trend": [...] }` },
        { method: 'GET', path: '/api/analytics/meter-usage/rooms', name: 'Meter Usage by Room', description: 'Electricity and water usage per room with totals and variance.', auth: 'both-read', authNote: 'Bearer  OR  API-Key (finance.read)', params: [{ name: 'building_id', type: 'integer', required: false, in: 'query', description: 'Filter by building' }, { name: 'month', type: 'string', required: false, in: 'query', description: 'Month (YYYY-MM)' }], response: `[{ "room_id": 51, "room_number": "P101", "electric_usage": 130, "electric_cost": 520000 }]` },
        { method: 'GET', path: '/api/analytics/meter-prediction', name: 'Meter Cost Prediction', description: 'Forecasted electricity and water usage/cost with confidence scores.', auth: 'both-read', authNote: 'Bearer  OR  API-Key (finance.read)', params: [{ name: 'building_id', type: 'integer', required: false, in: 'query', description: 'Filter by building' }, { name: 'months_ahead', type: 'integer', required: false, in: 'query', description: 'Months to forecast (default 3)' }], response: `{ "predictions": [{ "month": "2025-02", "electric": { "predicted_usage": 135, "predicted_cost": 540000, "confidence": 0.87 } }] }` },
      ]
    },
    {
      id: 'properties', label: 'Properties', color: 'teal',
      endpoints: [
        { method: 'GET', path: '/api/properties/export/buildings', name: 'List Buildings', description: 'All buildings with services_config and utility_pricing.', auth: 'both-read', authNote: 'Bearer  OR  API-Key (finance.read)', params: [], response: `[{ "id": 1, "name": "Building A", "services_config": {...}, "utility_pricing": {...} }]` },
        { method: 'GET', path: '/api/properties/export/rooms', name: 'List Rooms', description: 'All rooms with building info, status, rent, and inherited services config.', auth: 'both-read', authNote: 'Bearer  OR  API-Key (finance.read)', params: [{ name: 'building_id', type: 'integer', required: false, in: 'query', description: 'Filter by building ID' }, { name: 'status', type: 'string', required: false, in: 'query', description: 'available | occupied | maintenance | reserved' }], response: `[{ "id": 51, "room_number": "P101", "status": "occupied", "monthly_rent": 3500000, "building_name": "Building A" }]` },
      ]
    },
    {
      id: 'export', label: 'Export (finance.export)', color: 'rose',
      endpoints: [
        { method: 'GET', path: '/api/export/summary', name: 'Financial Summary Export', description: 'Aggregated financial summary: payments, expenses, occupancy, net income.', auth: 'apikey-export', authNote: 'Bearer  OR  API-Key (finance.export)', params: [{ name: 'year', type: 'integer', required: false, in: 'query', description: 'Year (defaults to current)' }, { name: 'month', type: 'integer', required: false, in: 'query', description: 'Month 1-12 (defaults to current)' }, { name: 'building_id', type: 'integer', required: false, in: 'query', description: 'Filter by building' }], response: `{ "period": {...}, "payments": {...}, "expenses": {...}, "occupancy": {...}, "net_income": 36000000 }` },
        { method: 'GET', path: '/api/export/payments', name: 'Payments Export', description: 'Bulk export of payment records. Supports JSON and CSV. Requires finance.export permission.', auth: 'apikey-export', authNote: 'Bearer  OR  API-Key (finance.export)', params: [{ name: 'start_date', type: 'string', required: false, in: 'query', description: 'Start month (YYYY-MM)' }, { name: 'end_date', type: 'string', required: false, in: 'query', description: 'End month (YYYY-MM)' }, { name: 'format', type: 'string', required: false, in: 'query', description: 'json (default) | csv' }], response: `{ "data": [...], "count": 1, "exported_at": "2025-01-15T10:30:00" }` },
        { method: 'GET', path: '/api/export/invoices', name: 'Invoices Export', description: 'Bulk export of invoice records in JSON or CSV format. Requires finance.export permission.', auth: 'apikey-export', authNote: 'Bearer  OR  API-Key (finance.export)', params: [{ name: 'format', type: 'string', required: false, in: 'query', description: 'json (default) | csv' }], response: `{ "data": [...], "count": 1 }` },
        { method: 'GET', path: '/api/export/meter-readings', name: 'Meter Readings Export', description: 'Bulk export of electricity and water meter readings. Requires finance.export permission.', auth: 'apikey-export', authNote: 'Bearer  OR  API-Key (finance.export)', params: [{ name: 'meter_type', type: 'string', required: false, in: 'query', description: 'electricity | water' }, { name: 'format', type: 'string', required: false, in: 'query', description: 'json (default) | csv' }], response: `{ "data": [...], "count": 1 }` },
      ]
    },
  ];

  const mappingData = [
    { financeField: 'gross_revenue',        apiSource: '/api/analytics/revenue → current_month.total_invoiced', description: 'Total billed before adjustments' },
    { financeField: 'net_revenue_expected',  apiSource: '/api/analytics/revenue → current_month.final_expected', description: 'After discounts/waivers applied' },
    { financeField: 'actual_collected',      apiSource: '/api/analytics/revenue → current_month.total_collected', description: 'Cash actually received' },
    { financeField: 'total_expenses',        apiSource: '/api/stats/profit → total_expenses', description: 'Sum of all operating expenses' },
    { financeField: 'net_profit',            apiSource: '/api/stats/profit → net_profit', description: 'Revenue minus total expenses' },
    { financeField: 'profit_margin_pct',     apiSource: '/api/stats/profit → profit_margin', description: 'Net profit as % of gross revenue' },
    { financeField: 'income_by_category',    apiSource: '/api/analytics/revenue/by-service', description: 'Rent, electricity, water, internet separately' },
    { financeField: 'expense_by_category',   apiSource: '/api/expenses/stats → by_category', description: 'Mapped by standard category keys' },
    { financeField: 'monthly_trend',         apiSource: '/api/analytics/revenue → monthly_trend', description: 'Income vs expense per month' },
    { financeField: 'collection_rate',       apiSource: '/api/stats → collection_rate', description: 'Payments collected / invoiced %' },
    { financeField: 'outstanding_balance',   apiSource: '/api/payments/grouped → outstanding', description: 'Uncollected amounts per building' },
    { financeField: 'adjustments_total',     apiSource: '/api/analytics/adjustments → total_amount', description: 'Total credits, waivers, discounts' },
    { financeField: 'utility_forecast',      apiSource: '/api/analytics/meter-prediction', description: 'Projected electricity & water cost' },
  ];

  const pullSequence = [
    { step: 1, endpoint: 'POST /api/token',                        purpose: 'Obtain Bearer token (admin/manager only)' },
    { step: 2, endpoint: 'GET /api/stats/profit',                  purpose: 'Full P&L: gross revenue, adjustments, net profit, margin' },
    { step: 3, endpoint: 'GET /api/analytics/revenue',             purpose: 'Current month + YTD + monthly trend + per-building' },
    { step: 4, endpoint: 'GET /api/analytics/revenue/by-service',  purpose: 'Income breakdown by service type' },
    { step: 5, endpoint: 'GET /api/expenses/stats',                purpose: 'Expense totals by category + monthly trend' },
    { step: 6, endpoint: 'GET /api/payments/grouped',              purpose: 'Collection rate and outstanding per building' },
    { step: 7, endpoint: 'GET /api/analytics/adjustments',         purpose: 'Discount/waiver/credit summaries' },
    { step: 8, endpoint: 'GET /api/analytics/meter-prediction',    purpose: 'Forecast utility costs for budgeting' },
  ];

  const enumData = [
    { name: 'PaymentStatus',               values: ['pending', 'partial', 'confirmed', 'completed', 'rejected', 'failed', 'refunded'] },
    { name: 'TransactionStatus',           values: ['pending', 'confirmed', 'partial', 'rejected'] },
    { name: 'AdjustmentType',             values: ['credit', 'waiver', 'discount'] },
    { name: 'AdjustmentStatus',           values: ['applied', 'pending', 'reversed'] },
    { name: 'ReadingType',                values: ['electricity (kWh)', 'water (m³)'] },
    { name: 'ReadingStatus',              values: ['pending', 'verified', 'rejected'] },
    { name: 'InvoiceStatus',              values: ['pending', 'paid', 'overdue', 'cancelled'] },
    { name: 'AdjustableServices',         values: ['internet', 'elevator', 'cleaning', 'washing'] },
    { name: 'MeteredServices (non-adjustable)', values: ['electricity', 'water'] },
  ];

  const codeExamples = {
    python: `import requests\n\nBASE_URL = "${baseUrl}"\nAPI_KEY = "YOUR_API_KEY"\nAPI_SECRET = "YOUR_API_SECRET"\n\ndef get_token(username, password):\n    r = requests.post(f"{baseUrl}/api/token",\n        data={"username": username, "password": password})\n    return r.json()["access_token"]\n\nTOKEN = get_token("admin", "password")\nAUTH_HEADERS = {"Authorization": f"Bearer {TOKEN}"}\nAPI_HEADERS  = {"X-API-Key": API_KEY, "X-API-Secret": API_SECRET}\n\npnl = requests.get(f"{baseUrl}/api/stats/profit", headers=AUTH_HEADERS).json()\nprint(f"Net Profit: {pnl['net_profit']:,.0f} ₫  |  Margin: {pnl['profit_margin']}%")\n\nrev = requests.get(f"{baseUrl}/api/analytics/revenue", headers=AUTH_HEADERS, params={"year": 2025, "month": 1}).json()\nprint(f"Collected: {rev['current_month']['total_collected']:,.0f} ₫")`,
    javascript: `const BASE_URL = "${baseUrl}";\nconst API_KEY = "YOUR_API_KEY";\nconst API_SECRET = "YOUR_API_SECRET";\n\nasync function getToken(username, password) {\n  const form = new URLSearchParams({ username, password });\n  const res = await fetch(\`\${BASE_URL}/api/token\`, { method: 'POST', body: form });\n  const data = await res.json();\n  return data.access_token;\n}\n\nconst TOKEN = await getToken('admin', 'password');\nconst AUTH = { Authorization: \`Bearer \${TOKEN}\` };\n\nconst pnl = await fetch(\`\${BASE_URL}/api/stats/profit\`, { headers: AUTH }).then(r => r.json());\nconsole.log('Net Profit:', pnl.net_profit.toLocaleString(), '₫');\n\nconst rev = await fetch(\`\${BASE_URL}/api/analytics/revenue?year=2025&month=1\`, { headers: AUTH }).then(r => r.json());\nconst financeData = {\n  grossRevenue: rev.current_month.total_invoiced,\n  actualCollected: rev.current_month.total_collected,\n  netProfit: pnl.net_profit,\n};\nconsole.log(financeData);`,
    curl: `# Obtain Bearer token\nTOKEN=$(curl -s -X POST "${baseUrl}/api/token" \\\n  -d "username=admin&password=yourpassword" | jq -r .access_token)\n\n# P&L Statement\ncurl -s "${baseUrl}/api/stats/profit" \\\n  -H "Authorization: Bearer $TOKEN" | jq '{net_profit, profit_margin}'\n\n# Revenue Dashboard\ncurl -s "${baseUrl}/api/analytics/revenue?year=2025&month=1" \\\n  -H "Authorization: Bearer $TOKEN" | jq '.current_month'\n\n# Export Summary (API-Key auth)\ncurl -s "${baseUrl}/api/export/summary?year=2025&month=1" \\\n  -H "X-API-Key: YOUR_API_KEY" \\\n  -H "X-API-Secret: YOUR_API_SECRET"\n\n# Export Payments as CSV\ncurl -s "${baseUrl}/api/export/payments?format=csv" \\\n  -H "X-API-Key: YOUR_API_KEY" \\\n  -H "X-API-Secret: YOUR_API_SECRET" \\\n  -o payments.csv`,
  };

  const docTabs = [
    { id: 'overview',      name: 'Overview' },
    { id: 'endpoints',     name: 'Endpoints' },
    { id: 'mapping',       name: 'App Mapping' },
    { id: 'pull-sequence', name: 'Data Pull Order' },
    { id: 'examples',      name: 'Code Examples' },
    { id: 'enums',         name: 'Enums & Types' },
    { id: 'errors',        name: 'Error Handling' },
  ];

  const groupColorMap = {
    violet: 'bg-violet-500/20 text-violet-400 border-violet-500/30',
    indigo: 'bg-indigo-500/20 text-indigo-400 border-indigo-500/30',
    cyan:   'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
    blue:   'bg-blue-500/20 text-blue-400 border-blue-500/30',
    teal:   'bg-teal-500/20 text-teal-400 border-teal-500/30',
    orange: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
    amber:  'bg-amber-500/20 text-amber-400 border-amber-500/30',
    emerald:'bg-emerald-500/20 text-emerald-400 border-emerald-500/30',
    purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    rose:   'bg-rose-500/20 text-rose-400 border-rose-500/30',
  };

  return (
    <div className="mt-8 bg-gradient-to-br from-slate-900 to-slate-800 rounded-2xl border border-slate-700 overflow-hidden">
      {/* Header */}
      <div className="p-6 border-b border-slate-700 bg-gradient-to-r from-teal-900/40 to-slate-900/40">
        <div className="flex items-center gap-3 mb-2">
          <div className="p-2 bg-teal-500/20 rounded-lg border border-teal-500/30">
            <Icons.Document size={22} className="text-teal-400" />
          </div>
          <div>
            <h4 className="text-xl font-bold text-white">Finance API Integration Guide</h4>
            <p className="text-slate-400 text-xs mt-0.5">Comprehensive reference for personal finance app integration</p>
          </div>
        </div>
        <div className="flex flex-wrap gap-2 mt-3">
          {[
            { label: `${endpointGroups.reduce((a, g) => a + g.endpoints.length, 0)} Endpoints`, color: 'bg-teal-500/20 text-teal-300' },
            { label: '2 Auth Methods',  color: 'bg-violet-500/20 text-violet-300' },
            { label: 'JSON + CSV',      color: 'bg-emerald-500/20 text-emerald-300' },
            { label: 'P&L + Analytics', color: 'bg-amber-500/20 text-amber-300' },
          ].map(b => <span key={b.label} className={`px-2 py-0.5 rounded text-xs font-medium ${b.color}`}>{b.label}</span>)}
        </div>
      </div>

      {/* Doc Tabs */}
      <div className="flex overflow-x-auto border-b border-slate-700 bg-slate-800/50">
        {docTabs.map((tab) => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-3 text-xs font-medium whitespace-nowrap transition-colors ${
              activeTab === tab.id
                ? 'text-teal-400 border-b-2 border-teal-400 bg-slate-800'
                : 'text-slate-400 hover:text-slate-300'
            }`}
          >
            {tab.name}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="p-6 overflow-y-auto max-h-[70vh]">

        {activeTab === 'overview' && (
          <div className="space-y-6">
            <div>
              <h5 className="text-base font-semibold text-white mb-3 flex items-center gap-2">
                <Icons.Shield size={18} className="text-green-400" /> Authentication Methods
              </h5>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-slate-950 rounded-xl p-4 border border-slate-800">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 bg-violet-500/20 text-violet-400 text-xs font-bold rounded">Bearer Token</span>
                    <span className="text-slate-500 text-xs">For direct API access</span>
                  </div>
                  <div className="font-mono text-xs space-y-1">
                    <div><span className="text-emerald-400">POST</span> <span className="text-teal-400">/api/token</span></div>
                    <div className="text-slate-400">Body: username + password</div>
                    <div className="text-slate-500 mt-2">Header: <span className="text-amber-300">Authorization: Bearer &lt;token&gt;</span></div>
                  </div>
                  <div className="mt-2 text-xs text-slate-500">Roles: admin, manager only</div>
                </div>
                <div className="bg-slate-950 rounded-xl p-4 border border-slate-800">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 bg-rose-500/20 text-rose-400 text-xs font-bold rounded">API-Key</span>
                    <span className="text-slate-500 text-xs">For third-party integrations</span>
                  </div>
                  <div className="font-mono text-xs space-y-1">
                    <div className="text-slate-400">Header: <span className="text-amber-300">X-API-Key: YOUR_KEY</span></div>
                    <div className="text-slate-400">Header: <span className="text-amber-300">X-API-Secret: YOUR_SECRET</span></div>
                  </div>
                  <div className="mt-3 space-y-1">
                    <div className="text-xs text-slate-400"><span className="text-rose-400 font-semibold">finance.read</span> — stats, analytics, payments, invoices, expenses, meter readings, adjustments, properties</div>
                    <div className="text-xs text-slate-400"><span className="text-rose-400 font-semibold">finance.export</span> — all <code className="font-mono text-amber-300">/api/export/*</code> endpoints (JSON &amp; CSV)</div>
                  </div>
                  <div className="mt-2 text-xs text-slate-500">Generate from API Profiles above</div>
                </div>
              </div>
            </div>
            <div>
              <h5 className="text-base font-semibold text-white mb-3">Base URL</h5>
              <div className="bg-slate-950 rounded-xl p-3 font-mono text-sm flex items-center justify-between border border-slate-800">
                <span className="text-teal-400">{baseUrl}</span>
                <CopyBtn text={baseUrl} id="baseurl" />
              </div>
            </div>
            <div>
              <h5 className="text-base font-semibold text-white mb-3">API Groups</h5>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                {endpointGroups.map(g => (
                  <div key={g.id} className={`rounded-xl p-3 border ${groupColorMap[g.color]}`}>
                    <div className="font-medium text-sm">{g.label}</div>
                    <div className="text-xs opacity-70 mt-0.5">{g.endpoints.length} endpoint{g.endpoints.length !== 1 ? 's' : ''}</div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h5 className="text-base font-semibold text-white mb-3">Response Formats</h5>
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-slate-950 rounded-xl p-4 border border-slate-800">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 bg-blue-500/20 text-blue-400 text-xs font-medium rounded">JSON</span>
                    <span className="text-slate-400 text-xs">Default</span>
                  </div>
                  <p className="text-slate-300 text-xs">Structured data with metadata. Best for programmatic processing and personal finance app ingestion.</p>
                </div>
                <div className="bg-slate-950 rounded-xl p-4 border border-slate-800">
                  <div className="flex items-center gap-2 mb-2">
                    <span className="px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs font-medium rounded">CSV</span>
                    <span className="text-slate-400 text-xs">?format=csv</span>
                  </div>
                  <p className="text-slate-300 text-xs">Spreadsheet-compatible. Available on all /api/export/* endpoints for bulk data analysis.</p>
                </div>
              </div>
            </div>
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4">
              <div className="text-amber-400 text-xs font-semibold mb-1">Currency Note</div>
              <p className="text-slate-300 text-xs">All monetary amounts are in Vietnamese Đồng (₫). Values are returned as integers (no decimal). Divide by 1,000,000 to convert to millions of ₫.</p>
            </div>
          </div>
        )}

        {activeTab === 'endpoints' && (
          <div className="space-y-6">
            {endpointGroups.map((group) => (
              <div key={group.id}>
                <div className={`flex items-center gap-2 mb-3 px-3 py-1.5 rounded-lg border w-fit ${groupColorMap[group.color]}`}>
                  <span className="text-sm font-bold">{group.label}</span>
                  <span className="text-xs opacity-70">({group.endpoints.length})</span>
                </div>
                <div className="space-y-3">
                  {group.endpoints.map((ep, idx) => {
                    const key = `${group.id}-${idx}`;
                    const isOpen = expandedEndpoint === key;
                    return (
                      <div key={key} className="bg-slate-950 rounded-xl overflow-hidden border border-slate-800">
                        <button
                          className="w-full p-4 flex items-center gap-3 text-left hover:bg-slate-900/50 transition-colors"
                          onClick={() => setExpandedEndpoint(isOpen ? null : key)}
                        >
                          <MethodBadge method={ep.method} />
                          <code className="text-teal-300 text-xs flex-1">{ep.path}</code>
                          <span className="text-slate-300 text-sm font-medium hidden md:block">{ep.name}</span>
                          {ep.auth === 'apikey-read'   && <span className="px-1.5 py-0.5 bg-rose-500/20 text-rose-400 text-xs rounded">API-Key (read)</span>}
                          {ep.auth === 'apikey-export' && <span className="px-1.5 py-0.5 bg-rose-500/20 text-rose-400 text-xs rounded">API-Key (export)</span>}
                          {ep.auth === 'bearer' && <span className="px-1.5 py-0.5 bg-violet-500/20 text-violet-400 text-xs rounded">Bearer</span>}
                          {ep.auth === 'both-read' && (
                            <span className="flex items-center gap-1">
                              <span className="px-1.5 py-0.5 bg-violet-500/20 text-violet-400 text-xs rounded">Bearer</span>
                              <span className="text-slate-500 text-xs">or</span>
                              <span className="px-1.5 py-0.5 bg-rose-500/20 text-rose-400 text-xs rounded">API-Key</span>
                            </span>
                          )}
                          {ep.auth === 'none' && <span className="px-1.5 py-0.5 bg-slate-500/20 text-slate-400 text-xs rounded">No Auth</span>}
                          <span className="text-slate-500 text-xs ml-2">{isOpen ? '▲' : '▼'}</span>
                        </button>
                        {isOpen && (
                          <div className="border-t border-slate-800">
                            <div className="p-4 border-b border-slate-800">
                              <p className="text-slate-300 text-sm mb-3">{ep.description}</p>
                              <div className="flex items-center gap-2 text-xs">
                                <span className="text-slate-500 font-semibold uppercase">Auth:</span>
                                {ep.auth === 'none'         && <span className="px-2 py-0.5 bg-slate-500/20 text-slate-400 rounded">No authentication required</span>}
                                {ep.auth === 'bearer'       && <span className="px-2 py-0.5 bg-violet-500/20 text-violet-400 rounded">Bearer JWT — admin or manager role required</span>}
                                {ep.auth === 'apikey-read'  && <span className="px-2 py-0.5 bg-rose-500/20 text-rose-400 rounded">API-Key with <code className="font-mono">finance.read</code> permission</span>}
                                {ep.auth === 'apikey-export'&& <span className="px-2 py-0.5 bg-rose-500/20 text-rose-400 rounded">API-Key with <code className="font-mono">finance.export</code> permission</span>}
                                {ep.auth === 'both-read' && (
                                  <>
                                    <span className="px-2 py-0.5 bg-violet-500/20 text-violet-400 rounded">Bearer JWT (admin/manager)</span>
                                    <span className="text-slate-500">or</span>
                                    <span className="px-2 py-0.5 bg-rose-500/20 text-rose-400 rounded">API-Key with <code className="font-mono">finance.read</code></span>
                                  </>
                                )}
                              </div>
                            </div>
                            {ep.params.length > 0 && (
                              <div className="p-4 border-b border-slate-800">
                                <h6 className="text-slate-400 text-xs font-semibold uppercase mb-3">Parameters</h6>
                                <div className="space-y-2">
                                  {ep.params.map((p, pi) => (
                                    <div key={pi} className="flex items-start gap-3 text-xs">
                                      <code className="text-amber-300 bg-slate-900 px-2 py-0.5 rounded min-w-[130px] shrink-0">{p.name}</code>
                                      <span className="text-slate-500 min-w-[50px] shrink-0">{p.type}</span>
                                      <span className="text-slate-600 min-w-[40px] shrink-0">{p.in}</span>
                                      {p.required ? <span className="text-rose-400 shrink-0">required</span> : <span className="text-slate-600 shrink-0">optional</span>}
                                      <span className="text-slate-400">{p.description}</span>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                            <div className="p-4">
                              <div className="flex items-center justify-between mb-2">
                                <h6 className="text-slate-400 text-xs font-semibold uppercase">Response Example</h6>
                                <CopyBtn text={ep.response} id={`ep-${key}`} />
                              </div>
                              <pre className="text-xs text-slate-300 overflow-x-auto bg-slate-900 rounded-lg p-3 max-h-64">
                                <code>{ep.response}</code>
                              </pre>
                            </div>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'mapping' && (
          <div className="space-y-6">
            <div>
              <h5 className="text-base font-semibold text-white mb-1">Field Mapping: AMS → Personal Finance App</h5>
              <p className="text-slate-400 text-xs mb-4">Map AMS API response fields directly to your finance app's data model.</p>
              <div className="bg-slate-950 rounded-xl overflow-hidden border border-slate-800">
                <div className="grid grid-cols-3 gap-0 p-3 bg-slate-900 text-xs font-semibold text-slate-400 uppercase">
                  <div>Finance App Field</div><div>AMS Endpoint → Field</div><div>Description</div>
                </div>
                {mappingData.map((row, i) => (
                  <div key={i} className={`grid grid-cols-3 gap-0 p-3 text-xs border-t border-slate-800 ${i % 2 !== 0 ? 'bg-slate-900/30' : ''}`}>
                    <div><code className="text-emerald-400">{row.financeField}</code></div>
                    <div><code className="text-teal-300 text-xs">{row.apiSource}</code></div>
                    <div className="text-slate-400">{row.description}</div>
                  </div>
                ))}
              </div>
            </div>
            <div>
              <h5 className="text-base font-semibold text-white mb-3">P&L Formula</h5>
              <div className="bg-slate-950 rounded-xl p-4 border border-slate-800 font-mono text-xs space-y-2">
                <div><span className="text-emerald-400">gross_invoice_amount</span> <span className="text-slate-500">= total billed (before adjustments)</span></div>
                <div><span className="text-slate-500">−</span> <span className="text-amber-400">total_adjustments</span> <span className="text-slate-500">= credits + waivers + discounts</span></div>
                <div className="border-t border-slate-700 pt-2"><span className="text-teal-400">= expected_revenue</span> <span className="text-slate-500">(after adjustments)</span></div>
                <div className="border-t border-slate-700 pt-2"><span className="text-slate-500">actual collected:</span> <span className="text-cyan-400">total_revenue</span></div>
                <div><span className="text-slate-500">−</span> <span className="text-rose-400">total_expenses</span> <span className="text-slate-500">= operating costs</span></div>
                <div className="border-t border-slate-700 pt-2"><span className="text-emerald-400 font-bold">= net_profit</span> <span className="text-slate-500">(profit_margin %)</span></div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'pull-sequence' && (
          <div className="space-y-4">
            <div>
              <h5 className="text-base font-semibold text-white mb-1">Recommended Data Pull Sequence</h5>
              <p className="text-slate-400 text-xs mb-4">Follow this order when syncing AMS data into your personal finance app for optimal results.</p>
            </div>
            {pullSequence.map((s) => (
              <div key={s.step} className="flex items-start gap-4 bg-slate-950 rounded-xl p-4 border border-slate-800">
                <div className="w-8 h-8 rounded-full bg-teal-500/20 border border-teal-500/40 flex items-center justify-center text-teal-400 font-bold text-sm shrink-0">{s.step}</div>
                <div>
                  <code className="text-teal-300 text-xs">{s.endpoint}</code>
                  <p className="text-slate-400 text-xs mt-1">{s.purpose}</p>
                </div>
              </div>
            ))}
            <div className="bg-teal-500/10 border border-teal-500/30 rounded-xl p-4">
              <div className="text-teal-400 text-xs font-semibold mb-1">Tip: Caching</div>
              <p className="text-slate-300 text-xs">Cache monthly data for 15 minutes. For real-time dashboards, poll <code className="text-amber-300">/api/stats</code> every 5 minutes and refresh full P&L hourly.</p>
            </div>
          </div>
        )}

        {activeTab === 'examples' && (
          <div className="space-y-6">
            {Object.entries(codeExamples).map(([lang, code]) => (
              <div key={lang} className="bg-slate-950 rounded-xl overflow-hidden border border-slate-800">
                <div className="flex items-center justify-between p-3 border-b border-slate-800">
                  <span className={`px-2 py-1 text-xs font-bold rounded ${
                    lang === 'curl' ? 'bg-orange-500/20 text-orange-400' :
                    lang === 'python' ? 'bg-blue-500/20 text-blue-400' :
                    'bg-yellow-500/20 text-yellow-400'
                  }`}>
                    {lang === 'curl' ? 'cURL' : lang.charAt(0).toUpperCase() + lang.slice(1)}
                  </span>
                  <CopyBtn text={code} id={`code-${lang}`} />
                </div>
                <pre className="p-4 text-xs text-slate-300 overflow-x-auto max-h-96">
                  <code>{code}</code>
                </pre>
              </div>
            ))}
          </div>
        )}

        {activeTab === 'enums' && (
          <div className="space-y-4">
            <h5 className="text-base font-semibold text-white mb-3">Enums & Data Types</h5>
            {enumData.map((e) => (
              <div key={e.name} className="bg-slate-950 rounded-xl p-4 border border-slate-800">
                <div className="text-slate-300 text-sm font-semibold mb-2">{e.name}</div>
                <div className="flex flex-wrap gap-2">
                  {e.values.map((v) => (
                    <code key={v} className="px-2 py-0.5 bg-teal-500/20 text-teal-300 text-xs rounded border border-teal-500/20">{v}</code>
                  ))}
                </div>
              </div>
            ))}
            <div>
              <h5 className="text-base font-semibold text-white mb-3 mt-4">Service IDs</h5>
              <div className="bg-slate-950 rounded-xl overflow-hidden border border-slate-800">
                {[
                  { id: 'rent',      label: 'Base Rent',   metered: false, adjustable: false, unit: 'fixed/month' },
                  { id: 'electric',  label: 'Electricity', metered: true,  adjustable: false, unit: 'kWh' },
                  { id: 'water',     label: 'Water',       metered: true,  adjustable: false, unit: 'm³' },
                  { id: 'internet',  label: 'Internet',    metered: false, adjustable: true,  unit: 'per person' },
                  { id: 'elevator',  label: 'Elevator',    metered: false, adjustable: true,  unit: 'per person' },
                  { id: 'cleaning',  label: 'Cleaning',    metered: false, adjustable: true,  unit: 'per person' },
                  { id: 'washing',   label: 'Washing',     metered: false, adjustable: true,  unit: 'per person' },
                ].map((s, i) => (
                  <div key={s.id} className={`flex items-center gap-4 p-3 text-xs border-t border-slate-800 ${i === 0 ? 'border-t-0' : ''} ${i % 2 !== 0 ? 'bg-slate-900/30' : ''}`}>
                    <code className="text-amber-300 min-w-[80px]">{s.id}</code>
                    <span className="text-slate-300 min-w-[100px]">{s.label}</span>
                    <span className="text-slate-500 min-w-[70px]">{s.unit}</span>
                    {s.metered    && <span className="px-1.5 py-0.5 bg-teal-500/20 text-teal-400 rounded">metered</span>}
                    {s.adjustable && <span className="px-1.5 py-0.5 bg-amber-500/20 text-amber-400 rounded">adjustable</span>}
                    {!s.adjustable && !s.metered && <span className="px-1.5 py-0.5 bg-slate-500/20 text-slate-400 rounded">fixed</span>}
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {activeTab === 'errors' && (
          <div className="space-y-6">
            <p className="text-slate-300 text-sm">The API uses standard HTTP status codes to indicate success or failure.</p>
            <div className="space-y-3">
              {[
                { code: '200', status: 'OK',                    description: 'Request successful',                                        color: 'emerald' },
                { code: '400', status: 'Bad Request',           description: 'Invalid parameters or malformed request body',             color: 'amber' },
                { code: '401', status: 'Unauthorized',          description: 'Missing, expired, or invalid credentials',                  color: 'rose' },
                { code: '403', status: 'Forbidden',             description: 'Role or API profile lacks required permissions',           color: 'rose' },
                { code: '404', status: 'Not Found',             description: 'Endpoint or resource does not exist',                     color: 'amber' },
                { code: '422', status: 'Unprocessable Entity',  description: 'Validation error on request body fields',                  color: 'amber' },
                { code: '429', status: 'Too Many Requests',     description: 'Rate limit exceeded — implement exponential backoff',       color: 'amber' },
                { code: '500', status: 'Internal Server Error', description: 'Unexpected server-side error',                            color: 'rose' },
              ].map((err) => (
                <div key={err.code} className="flex items-center gap-4 bg-slate-950 rounded-xl p-4 border border-slate-800">
                  <span className={`px-3 py-1 rounded font-mono text-sm font-bold shrink-0 ${
                    err.color === 'emerald' ? 'bg-emerald-500/20 text-emerald-400' :
                    err.color === 'amber'   ? 'bg-amber-500/20 text-amber-400' :
                    'bg-rose-500/20 text-rose-400'
                  }`}>{err.code}</span>
                  <div>
                    <span className="text-white font-medium text-sm">{err.status}</span>
                    <p className="text-slate-400 text-xs mt-0.5">{err.description}</p>
                  </div>
                </div>
              ))}
            </div>
            <div>
              <h5 className="text-white font-semibold text-sm mb-3">Error Response Format</h5>
              <div className="bg-slate-950 rounded-xl p-4 border border-slate-800">
                <pre className="text-xs text-slate-300">{`// Simple error\n{ "detail": "Invalid API credentials" }\n\n// Validation error (422)\n{\n  "detail": [\n    { "loc": ["body", "service_id"], "msg": "field required", "type": "value_error.missing" }\n  ]\n}\n\n// Permission error\n{ "detail": "Export permission not granted", "error_code": "PERMISSION_DENIED" }`}</pre>
              </div>
            </div>
            <div>
              <h5 className="text-white font-semibold text-sm mb-3">Retry Strategy</h5>
              <div className="bg-slate-950 rounded-xl p-4 border border-slate-800 text-xs text-slate-300 space-y-1">
                <div>• On <code className="text-amber-300">429</code>: wait for <code className="text-amber-300">Retry-After</code> header, then retry</div>
                <div>• On <code className="text-amber-300">5xx</code>: exponential backoff — retry at 1s, 2s, 4s (max 3 retries)</div>
                <div>• On <code className="text-amber-300">401</code>: refresh Bearer token via <code className="text-teal-300">POST /api/token</code></div>
                <div>• On <code className="text-amber-300">403</code>: check API profile permissions — do not retry</div>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

// ── API Profiles Settings ─────────────────────────────────────────────────────
const APIProfilesSettings = () => {
  const [profiles, setProfiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showSecretModal, setShowSecretModal] = useState(null);
  const [showSecret, setShowSecret] = useState(false);
  const [newProfile, setNewProfile] = useState({
    name: '', description: '', rate_limit: 100,
    permissions: {
      finance:    { read: true, export: true },
      properties: { read: false },
      contracts:  { read: false },
      tickets:    { read: false },
    },
  });

  const DEFAULT_PERMISSIONS = {
    finance: { read: true, export: true }, properties: { read: false }, contracts: { read: false }, tickets: { read: false },
  };

  const fetchProfiles = async () => {
    try {
      const response = await api.get('/api/api-profiles');
      const data = response.data;
      setProfiles(Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : []);
    } catch {
      toast.error(t('settings:toast.apiProfilesLoadFailed'));
      setProfiles([]);
    } finally {
      setLoading(false);
    }
  };

  React.useEffect(() => { fetchProfiles(); }, []);

  const handleCreateProfile = async () => {
    try {
      const response = await api.post('/api/api-profiles', newProfile);
      setProfiles(prev => [response.data, ...(Array.isArray(prev) ? prev : [])]);
      setShowSecretModal(response.data);
      setShowCreateModal(false);
      setNewProfile({ name: '', description: '', rate_limit: 100, permissions: DEFAULT_PERMISSIONS });
      toast.success(t('settings:toast.apiProfileCreated'));
    } catch (error) {
      toast.error(error.response?.data?.detail || t('settings:toast.apiProfileCreateFailed'));
    }
  };

  const handleDeleteProfile = async (profileId) => {
    if (!window.confirm('Delete this API profile? This action cannot be undone.')) return;
    try {
      await api.delete(`/api/api-profiles/${profileId}`);
      setProfiles(prev => (Array.isArray(prev) ? prev : []).filter(p => p.id !== profileId));
      toast.success(t('settings:toast.apiProfileDeleted'));
    } catch {
      toast.error(t('settings:toast.apiProfileDeleteFailed'));
    }
  };

  const handleRegenerateCredentials = async (profileId) => {
    if (!window.confirm('Regenerate credentials? The old credentials will stop working immediately.')) return;
    try {
      const response = await api.post(`/api/api-profiles/${profileId}/regenerate`);
      setProfiles(prev => (Array.isArray(prev) ? prev : []).map(p => p.id === profileId ? { ...response.data, api_secret: undefined } : p));
      setShowSecretModal(response.data);
      toast.success(t('settings:toast.credentialsRegenerated'));
    } catch {
      toast.error(t('settings:toast.credentialsRegenerateFailed'));
    }
  };

  const handleToggleActive = async (profile) => {
    try {
      const response = await api.put(`/api/api-profiles/${profile.id}`, { is_active: !profile.is_active });
      setProfiles(prev => (Array.isArray(prev) ? prev : []).map(p => p.id === profile.id ? response.data : p));
      toast.success(`API profile ${response.data.is_active ? 'activated' : 'deactivated'}`);
    } catch {
      toast.error(t('settings:toast.apiProfileUpdateFailed'));
    }
  };

  const copyToClipboard = (text, label) => {
    navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Icons.Spinner size={28} className="animate-spin text-teal-600" />
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6 pb-4 border-b border-stone-100">
        <div>
          <h3 className="text-lg font-bold text-stone-800">API Profiles</h3>
          <p className="text-sm text-stone-500 mt-1">Manage API access for external applications</p>
        </div>
        <Button kind="primary" iconLeft={<Icons.Plus size={16} />} onClick={() => setShowCreateModal(true)}>
          Create Profile
        </Button>
      </div>

      {(!Array.isArray(profiles) || profiles.length === 0) ? (
        <div className="text-center py-12 bg-stone-50 rounded-2xl border border-stone-100">
          <Icons.Upload size={40} className="text-stone-300 mx-auto mb-3" />
          <h4 className="text-base font-medium text-stone-600 mb-2">No API Profiles</h4>
          <p className="text-stone-500 text-sm mb-4">Create an API profile to allow external applications to access your data</p>
          <Button kind="primary" size="sm" onClick={() => setShowCreateModal(true)}>Create Your First Profile</Button>
        </div>
      ) : (
        <div className="space-y-4">
          {(Array.isArray(profiles) ? profiles : []).filter(p => p?.id).map((profile) => (
            <div
              key={profile.id}
              className={`p-5 rounded-2xl border transition-all ${
                profile.is_active
                  ? 'bg-white border-stone-200 hover:border-teal-200 hover:shadow-md'
                  : 'bg-stone-50 border-stone-200 opacity-75'
              }`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-base font-semibold text-stone-800">{profile.name || 'Unnamed Profile'}</h4>
                    <span className={`px-2 py-0.5 text-xs font-medium rounded-full ${
                      profile.is_active ? 'bg-green-100 text-green-700' : 'bg-stone-200 text-stone-600'
                    }`}>
                      {profile.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </div>
                  {profile.description && <p className="text-sm text-stone-500 mb-3">{profile.description}</p>}

                  {profile.api_key && (
                    <div className="flex items-center gap-2 mb-3">
                      <span className="text-sm text-stone-500">API Key:</span>
                      <code className="px-2 py-1 bg-stone-100 rounded text-sm font-mono text-stone-700">
                        {profile.api_key.substring(0, 12)}...{profile.api_key.substring(profile.api_key.length - 8)}
                      </code>
                      <button onClick={() => copyToClipboard(profile.api_key, 'API Key')} className="p-1 text-stone-400 hover:text-teal-600 transition-colors" title="Copy API Key">
                        <Icons.Copy size={16} />
                      </button>
                    </div>
                  )}

                  <div className="flex flex-wrap gap-2 mb-3">
                    {(() => {
                      let perms = profile.permissions || {};
                      if (typeof perms === 'string') { try { perms = JSON.parse(perms); } catch { perms = {}; } }
                      return Object.entries(perms).map(([key, p]) =>
                        p && (p.read || p.export) ? (
                          <span key={key} className="px-2 py-1 bg-teal-50 text-teal-700 text-xs font-medium rounded-lg">
                            {key}: {[p.read && 'read', p.export && 'export'].filter(Boolean).join(', ')}
                          </span>
                        ) : null
                      );
                    })()}
                  </div>

                  <div className="flex items-center gap-4 text-sm text-stone-500">
                    <span>Rate Limit: {profile.rate_limit}/min</span>
                    <span>Requests: {profile.total_requests || 0}</span>
                    {profile.last_used_at && <span>Last Used: {new Date(profile.last_used_at).toLocaleDateString()}</span>}
                  </div>
                </div>

                <div className="flex items-center gap-1 ml-4">
                  <button onClick={() => handleToggleActive(profile)}
                    className={`p-2 rounded-lg transition-colors ${profile.is_active ? 'text-amber-600 hover:bg-amber-50' : 'text-green-600 hover:bg-green-50'}`}
                    title={profile.is_active ? 'Deactivate' : 'Activate'}
                  >
                    {profile.is_active ? <Icons.EyeOff size={18} /> : <Icons.Eye size={18} />}
                  </button>
                  <button onClick={() => handleRegenerateCredentials(profile.id)}
                    className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors" title="Regenerate Credentials"
                  >
                    <Icons.Refresh size={18} />
                  </button>
                  <button onClick={() => handleDeleteProfile(profile.id)}
                    className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors" title="Delete Profile"
                  >
                    <Icons.Trash size={18} />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      <APIDocumentation />

      {/* Create Profile Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto border border-stone-200">
            <div className="p-6 border-b border-stone-100 flex items-center justify-between sticky top-0 bg-white">
              <h3 className="text-lg font-bold text-stone-800">Create API Profile</h3>
              <button onClick={() => setShowCreateModal(false)} className="p-2 text-stone-400 hover:text-stone-600 hover:bg-stone-100 rounded-lg">
                <Icons.X size={18} />
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className={LABEL_CLS}>Profile Name *</label>
                <input type="text" value={newProfile.name}
                  onChange={(e) => setNewProfile({ ...newProfile, name: e.target.value })}
                  className={INPUT_CLS} placeholder="e.g., Accounting System Integration"
                />
              </div>
              <div>
                <label className={LABEL_CLS}>Description</label>
                <textarea value={newProfile.description}
                  onChange={(e) => setNewProfile({ ...newProfile, description: e.target.value })}
                  className="w-full px-3 py-2 text-sm border border-stone-300 rounded-md bg-white text-stone-900 focus:outline-none focus:border-teal-600 focus:ring-1 focus:ring-teal-600"
                  rows={2} placeholder="What is this API profile used for?"
                />
              </div>
              <div>
                <label className={LABEL_CLS}>Rate Limit (requests/minute)</label>
                <input type="number" value={newProfile.rate_limit} min={1} max={1000}
                  onChange={(e) => setNewProfile({ ...newProfile, rate_limit: parseInt(e.target.value) || 100 })}
                  className={INPUT_CLS}
                />
              </div>
              <div>
                <label className={LABEL_CLS}>Permissions</label>
                <p className="text-xs text-stone-400 mb-3">Choose which data this API key can access via <code className="bg-stone-100 px-1 rounded">X-API-Key</code> + <code className="bg-stone-100 px-1 rounded">X-API-Secret</code> headers.</p>

                <div className="border border-stone-200 rounded-xl overflow-hidden mb-2">
                  <div className="px-4 py-3 bg-stone-50 border-b border-stone-200">
                    <span className="font-semibold text-stone-800 text-sm">Finance</span>
                    <span className="ml-2 text-xs text-stone-400">Core financial data</span>
                  </div>
                  <div className="divide-y divide-stone-100">
                    <div className="flex items-start gap-3 px-4 py-3">
                      <input type="checkbox" id="finance-read" checked={newProfile.permissions.finance?.read || false}
                        onChange={(e) => setNewProfile({ ...newProfile, permissions: { ...newProfile.permissions, finance: { ...newProfile.permissions.finance, read: e.target.checked } } })}
                        className="accent-teal-600 mt-0.5"
                      />
                      <label htmlFor="finance-read" className="cursor-pointer">
                        <div className="text-sm font-medium text-stone-700">Read <span className="ml-1 px-1.5 py-0.5 bg-teal-50 text-teal-600 text-xs rounded font-mono">finance.read</span></div>
                        <div className="text-xs text-stone-400 mt-0.5">Stats &amp; P&amp;L · Analytics · Payments · Invoices · Expenses · Meter Readings · Adjustments</div>
                      </label>
                    </div>
                    <div className="flex items-start gap-3 px-4 py-3">
                      <input type="checkbox" id="finance-export" checked={newProfile.permissions.finance?.export || false}
                        onChange={(e) => setNewProfile({ ...newProfile, permissions: { ...newProfile.permissions, finance: { ...newProfile.permissions.finance, export: e.target.checked } } })}
                        className="accent-teal-600 mt-0.5"
                      />
                      <label htmlFor="finance-export" className="cursor-pointer">
                        <div className="text-sm font-medium text-stone-700">Export <span className="ml-1 px-1.5 py-0.5 bg-red-50 text-red-600 text-xs rounded font-mono">finance.export</span></div>
                        <div className="text-xs text-stone-400 mt-0.5">Bulk export of payments, invoices, meter readings, and financial summary (JSON &amp; CSV)</div>
                      </label>
                    </div>
                  </div>
                </div>

                <div className="space-y-1.5">
                  {[
                    { key: 'properties', label: 'Properties', desc: 'Buildings and room data' },
                    { key: 'contracts',  label: 'Contracts',  desc: 'Rental contracts and lease data' },
                    { key: 'tickets',    label: 'Tickets',    desc: 'Maintenance and support tickets' },
                  ].map(({ key, label, desc }) => (
                    <div key={key} className="flex items-start gap-3 p-3 border border-stone-200 rounded-xl">
                      <input type="checkbox" id={`${key}-read`} checked={newProfile.permissions[key]?.read || false}
                        onChange={(e) => setNewProfile({ ...newProfile, permissions: { ...newProfile.permissions, [key]: { ...newProfile.permissions[key], read: e.target.checked } } })}
                        className="accent-teal-600 mt-0.5"
                      />
                      <label htmlFor={`${key}-read`} className="cursor-pointer">
                        <div className="text-sm font-medium text-stone-700">{label} <span className="ml-1 px-1.5 py-0.5 bg-stone-100 text-stone-500 text-xs rounded font-mono">{key}.read</span></div>
                        <div className="text-xs text-stone-400 mt-0.5">{desc}</div>
                      </label>
                    </div>
                  ))}
                </div>

                <div className="mt-3 p-3 bg-amber-50 border border-amber-100 rounded-xl">
                  <p className="text-xs text-amber-700"><span className="font-semibold">Note:</span> Individual payment records, transactions, and write operations (POST/PUT/DELETE) always require a Bearer token — they are never accessible via API key.</p>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-stone-100 flex justify-end gap-3">
              <Button kind="ghost" onClick={() => setShowCreateModal(false)}>Cancel</Button>
              <Button kind="primary" disabled={!newProfile.name.trim()} onClick={handleCreateProfile}>Create Profile</Button>
            </div>
          </div>
        </div>
      )}

      {/* Show Secret Modal */}
      {showSecretModal && (
        <div className="fixed inset-0 bg-stone-900/60 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-lg w-full border border-stone-200">
            <div className="p-6 border-b border-stone-100">
              <h3 className="text-lg font-bold text-stone-800">API Credentials</h3>
              <p className="text-sm text-amber-600 mt-1 flex items-center gap-2">
                <Icons.Shield size={16} />
                Save these credentials now. The secret will not be shown again.
              </p>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <label className={LABEL_CLS}>API Key</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-stone-100 rounded-lg text-sm font-mono text-stone-700 break-all">
                    {showSecretModal.api_key}
                  </code>
                  <button onClick={() => copyToClipboard(showSecretModal.api_key, 'API Key')} className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors">
                    <Icons.Copy size={18} />
                  </button>
                </div>
              </div>
              <div>
                <label className={LABEL_CLS}>API Secret</label>
                <div className="flex items-center gap-2">
                  <code className="flex-1 px-3 py-2 bg-stone-100 rounded-lg text-sm font-mono text-stone-700 break-all">
                    {showSecret ? showSecretModal.api_secret : '•'.repeat(32)}
                  </code>
                  <button onClick={() => setShowSecret(!showSecret)} className="p-2 text-stone-400 hover:text-stone-600 rounded-lg transition-colors">
                    {showSecret ? <Icons.EyeOff size={18} /> : <Icons.Eye size={18} />}
                  </button>
                  <button onClick={() => copyToClipboard(showSecretModal.api_secret, 'API Secret')} className="p-2 text-teal-600 hover:bg-teal-50 rounded-lg transition-colors">
                    <Icons.Copy size={18} />
                  </button>
                </div>
              </div>
            </div>
            <div className="p-6 border-t border-stone-100 flex justify-end">
              <Button kind="primary" onClick={() => { setShowSecretModal(null); setShowSecret(false); }}>Done</Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Settings;
